import {
  createEntityAdapter,
  createSlice,
  EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { RootState } from "../rootReducer";
import { CookingHistory } from "../../models/cooking-history";
import { generateId } from "../../utils/generateId";
import { updateCookingProgress } from "./cookingProgressSlice";
import { makeSyncUpsert } from "../syncUpsert";

export const cookingHistoryAdapter = createEntityAdapter<CookingHistory>();
type CookingHistoryState = EntityState<CookingHistory, string>;
const initialState: CookingHistoryState = cookingHistoryAdapter.getInitialState();

export const cookingHistorySlice = createSlice({
  name: "cookingHistory",
  initialState,
  reducers: {
    upsertManyFromSync: makeSyncUpsert(cookingHistoryAdapter),
    // Log a cook directly (e.g. "Record a cook" from the recipe view), without
    // going through cook mode.
    recordCook: {
      reducer(state: CookingHistoryState, action: PayloadAction<CookingHistory>) {
        cookingHistoryAdapter.addOne(state, action.payload);
      },
      prepare(payload: {
        recipeId: string;
        servingMultiplier?: number;
        completedAt?: string;
      }) {
        return {
          payload: {
            id: generateId(),
            recipeId: payload.recipeId,
            servingMultiplier: payload.servingMultiplier ?? 1,
            completedAt: payload.completedAt ?? new Date().toISOString(),
          },
        };
      },
    },
    // Soft-delete a single cook (swipe-to-delete on the history page).
    removeCookingHistory(state: CookingHistoryState, action: PayloadAction<string>) {
      const h = state.entities[action.payload];
      if (h && !h.deletedAt) {
        cookingHistoryAdapter.updateOne(state, {
          id: action.payload,
          changes: { deletedAt: new Date().toISOString() },
        });
      }
    },
    // Soft-delete every cook for a recipe ("Clear history" on the history page).
    clearCookingHistoryForRecipe(
      state: CookingHistoryState,
      action: PayloadAction<string>,
    ) {
      const now = new Date().toISOString();
      const updates = (Object.values(state.entities) as CookingHistory[])
        .filter((h) => h && h.recipeId === action.payload && !h.deletedAt)
        .map((h) => ({ id: h.id, changes: { deletedAt: now } }));
      if (updates.length) cookingHistoryAdapter.updateMany(state, updates);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateCookingProgress, (state, action) => {
      // Only include cooking progress that contains a recipeId and is completed
      if (action.payload.status !== "completed" || !action.payload.recipeId) return;

      const id = generateId();
      const completedAt = new Date().toISOString();

      cookingHistoryAdapter.addOne(state, {
        id,
        recipeId: action.payload.recipeId!,
        servingMultiplier: action.payload.servingMultiplier!,
        completedAt,
      });
    });
  },
});

export const {
  selectAll: selectAllCookingHistory,
  selectById: selectCookingHistoryById,
} = cookingHistoryAdapter.getSelectors((state: RootState) => state.cookingHistory);

// Number of completed cooks (non-deleted history rows). Used as the engagement
// signal for the review prompt; every history row represents a completed cook.
export const selectCompletedCookCount = (state: RootState): number =>
  selectAllCookingHistory(state).filter((h) => !h.deletedAt).length;

export const {
  recordCook,
  removeCookingHistory,
  clearCookingHistoryForRecipe,
  upsertManyFromSync: upsertCookingHistoryFromSync,
} = cookingHistorySlice.actions;

export default cookingHistorySlice.reducer;
